/**
 * What the app shows when it has crashed (7HAI-1224).
 *
 * The fallback was `<p>Something went wrong.</p>` — no reload, no way back, no
 * hint. Whatever caused the crash, the user's only exit was to edit the URL by
 * hand, which is not an exit most people find.
 *
 * A crash needs two things from this screen. A way FORWARD, because being stuck
 * on a dead page is a worse outcome than any error; and enough information to
 * report it, because "something went wrong" tells support nothing.
 *
 * IT OFFERS A HARD RELOAD, NOT A RE-RENDER. Re-rendering the same broken tree
 * usually produces the same crash, and the commonest cause here is a page whose
 * code is out of date with the server. Fetching the document again is what
 * actually fixes that.
 */
import React from "react";
import { useTranslation } from "react-i18next";

import { isChunkLoadError } from "../utils/lazyWithRetry";

import "./AppErrorFallback.css";

export interface AppErrorFallbackProps {
  /** The caught error, as Sentry's boundary hands it over. */
  error?: unknown;
  /** Sentry's event id, when there is one — the only thing that ties a user's
   *  report to the trace support will actually read. */
  eventId?: string;
}

const AppErrorFallback: React.FC<AppErrorFallbackProps> = ({ error, eventId }) => {
  const { t } = useTranslation();

  // A stale chunk is not a fault the user should be apologised to for — it just
  // means the app was updated while their tab was open. Saying so plainly stops
  // an ordinary deploy looking like a broken product.
  const stale = isChunkLoadError(error);

  return (
    <div className="app-error" role="alert">
      <h1 className="app-error__title">{stale ? t("appError.staleTitle") : t("appError.title")}</h1>
      <p className="app-error__body">{stale ? t("appError.staleBody") : t("appError.body")}</p>

      <div className="app-error__actions">
        <button
          type="button"
          className="app-error__reload"
          onClick={() => window.location.reload()}
        >
          {t("appError.reload")}
        </button>
        {/* An absolute href, not a router link: the router lives inside the
            tree that just crashed. A full navigation is the only thing that can
            be relied on from here. */}
        <a className="app-error__login" href="/login">
          {t("appError.toLogin")}
        </a>
      </div>

      {eventId && (
        <p className="app-error__ref">
          {t("appError.reference")} <code>{eventId}</code>
        </p>
      )}
    </div>
  );
};

export default AppErrorFallback;
